// =============================================================================
// Deep links — link ↔ in-app path conversion + a pending-link stash
// =============================================================================
// A "deep link" is just one of our React Router paths with a scheme/origin
// glued on the front. The desktop wrapper (Chat.Doof Desktop repo) registers
// the `chatdoof://` scheme AND intercepts clicks on `https://chat.doof.lol/*`,
// then hands the full url to the renderer via `window.electronAPI.onDeepLink`.
// The renderer normalizes it back to a path here and navigates.
//
// Design rule: linkToPath() and pathToShareLink() are inverses. Keep them that
// way so the link grammar and the router never drift apart — there is no
// translation table, the path IS the link.
// =============================================================================

/** Origin baked into shareable links we emit (copy-link, notifications). */
export const SHARE_ORIGIN = 'https://chat.doof.lol';

const CUSTOM_SCHEME = 'chatdoof:';
// Hosts we accept on an inbound https deep link. Anything else is rejected so a
// crafted link can't bounce navigation somewhere unexpected.
const ALLOWED_HTTP_HOSTS = new Set(['chat.doof.lol']);

function isDevHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1';
}

/**
 * Normalize an inbound deep link to an in-app router path.
 *
 * Accepts `chatdoof://...` (the desktop scheme) and `https://chat.doof.lol/...`
 * (an intercepted universal link), plus localhost for dev. Returns the in-app
 * path including any `?query`/`#hash` (e.g. `?m=<messageId>` for jump-to), or
 * `null` for anything unrecognized — callers should fall back to `/` (or just
 * ignore) on null rather than navigating an untrusted string.
 */
export function linkToPath(raw: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }

  let path: string;
  if (url.protocol === CUSTOM_SCHEME) {
    // chatdoof://s/123/general → host="s", pathname="/123/general".
    // Reassemble the authority + pathname into a single app path.
    path = `/${url.host}${url.pathname}`;
  } else if (url.protocol === 'https:' || url.protocol === 'http:') {
    if (!ALLOWED_HTTP_HOSTS.has(url.hostname) && !isDevHost(url.hostname)) return null;
    path = url.pathname || '/';
  } else {
    return null;
  }

  // Must be an absolute in-app path — never protocol-relative ("//evil.com")
  // or otherwise pointing off-origin. Browsers read "/\evil.com" as
  // "//evil.com", and react-router 6 doesn't normalize it (GHSA-wrjc-x8rr-h8h6),
  // so reject a backslash in the second slot too.
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;

  return `${path}${url.search}${url.hash}`;
}

/**
 * Build a shareable universal link for an in-app path. This is what copy-link
 * buttons, the notification feed, and push payloads should emit — it works for
 * browser users (opens the website) and desktop users (intercepted into the
 * app) alike. We deliberately do NOT emit `chatdoof://` here; the custom scheme
 * is desktop-only and dead-clicks in a browser.
 */
export function pathToShareLink(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${SHARE_ORIGIN}${p}`;
}

// -----------------------------------------------------------------------------
// Pending-link stash
// -----------------------------------------------------------------------------
// A deep link can arrive while the user is signed out (fresh install, expired
// token). We stash the target path module-side (survives the AuthScreen ↔
// authenticated-shell remount) and the deep-link handler consumes it once the
// user is authed.

let pendingPath: string | null = null;

/** Stash a path to navigate to once the user is authenticated. */
export function setPendingDeepLink(path: string | null): void {
  pendingPath = path;
}

/** Read and clear the stashed deep-link path, if any. */
export function takePendingDeepLink(): string | null {
  const p = pendingPath;
  pendingPath = null;
  return p;
}
